import { ConfirmDialog } from "src/components/ui";
import type { EngagementFieldsFragment, IdentityFieldsFragment } from "src/graphql/generated";
import { formatOnBusinessCalendar } from "src/lib/dates";

type Engagement = EngagementFieldsFragment;

/** The person's system access, and when it was last given and removed. */
export type PersonAccess = Pick<
  IdentityFieldsFragment,
  "hasSystemAccess" | "activatedAt" | "deactivatedAt"
>;

/**
 * Whether Remove system access is offered: the person has system access and a current
 * term keeps them in Okta. The caller checks the viewer may end every current term,
 * which removing their access takes.
 */
export function mayDeactivateInOkta(
  person: PersonAccess,
  currentTerms: readonly Engagement[]
): boolean {
  return person.hasSystemAccess && currentTerms.length > 0;
}

/**
 * Whether the person is without system access with no removal on record: created
 * without it, or here before system access was recorded, which started everybody
 * without it. Giving it to them is a grant rather than a restore.
 */
export function systemAccessNeverGranted(person: PersonAccess): boolean {
  return !person.hasSystemAccess && !person.deactivatedAt;
}

/**
 * Removing the person's system access apart from when their contracts end. It takes
 * effect in the request that confirms it: Okta is told before anything is saved, and
 * a removal Okta refuses saves nothing. Their terms stay current, and End engagement
 * is still how each ends.
 */
export function DeactivateInOktaDialog({
  open,
  personName,
  busy,
  onCancel,
  onConfirm
}: {
  open: boolean;
  personName: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      open={open}
      title="Remove system access?"
      confirmLabel="Remove system access"
      busy={busy}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <p>
        This removes {personName}&apos;s system access now by taking them out of Okta. Their
        contracts carry on: they stay current, and End engagement is still how each ends.
      </p>
      <p className="mt-3 text-xs text-slate-500">
        If Okta does not accept the removal, nothing is saved and they keep access. Once removed,
        only Restore system access gives it back. Every step is recorded in the history below.
      </p>
    </ConfirmDialog>
  );
}

/**
 * When the person's system access was given, while they have it, or removed, once it
 * is off, as the line beside their access says it.
 *
 * A removal says only that access was removed, never that a request was sent: people
 * ended by the bulk import were never in Okta, and theirs is recorded with nothing sent.
 */
export function systemAccessLines(person: PersonAccess): string[] {
  if (person.hasSystemAccess) {
    return person.activatedAt ? [`Since ${formatOnBusinessCalendar(person.activatedAt)}`] : [];
  }
  return person.deactivatedAt ? [`Removed ${formatOnBusinessCalendar(person.deactivatedAt)}`] : [];
}
